import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2 } from 'lucide-react'
import { CanoneRicorsivo, CanoneUpdate } from '../../types/offerte'

interface SortableCanoneRowProps {
  canone: CanoneRicorsivo
  disabled: boolean
  onUpdate: (canoneId: string, updates: CanoneUpdate) => void
  onUpdateNota: (canoneId: string, descrizione: string) => void
  onDelete: (canoneId: string) => void
  formatCurrency: (value: number | string) => string
}

export function SortableCanoneRow({
  canone,
  disabled,
  onUpdate,
  onUpdateNota,
  onDelete,
  formatCurrency
}: SortableCanoneRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: canone.id, disabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  const isLibero = canone.tipo === 'voce' && !canone.listino_id

  const [localQta, setLocalQta] = useState(String(canone.quantita ?? ''))
  const [localSconto, setLocalSconto] = useState(String(canone.sconto_percentuale || '0'))
  const [localNota, setLocalNota] = useState(canone.descrizione)
  const [localPrezzo, setLocalPrezzo] = useState(String(canone.prezzo_unitario ?? ''))
  const [localDescrizione, setLocalDescrizione] = useState(canone.descrizione)

  const FREQ_LABELS: Record<string, string> = {
    giorni: 'gg', settimane: 'sett', mesi: 'mesi', anni: 'anni'
  }

  if (canone.tipo === 'nota') {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className="bg-amber-50/50 border-l-4 border-amber-300"
      >
        <td className="py-2 px-1 w-8">
          {!disabled && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
        </td>
        <td colSpan={7} className="py-2 px-2">
          {disabled ? (
            <span className="text-sm text-gray-700 italic">{canone.descrizione}</span>
          ) : (
            <input
              type="text"
              value={localNota}
              onChange={(e) => setLocalNota(e.target.value)}
              onBlur={() => {
                if (localNota !== canone.descrizione) {
                  onUpdateNota(canone.id, localNota)
                }
              }}
              placeholder="Nota / Sezione..."
              className="w-full px-2 py-1 text-sm italic text-gray-700 bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          )}
        </td>
        {!disabled && (
          <td className="py-2 text-right px-2">
            <button
              onClick={() => onDelete(canone.id)}
              className="text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </td>
        )}
      </tr>
    )
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-indigo-50/30 ${isLibero ? 'bg-indigo-50/20' : ''}`}
    >
      <td className="py-3 px-1 w-8">
        {!disabled && (
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
      </td>
      <td className="py-3">
        {isLibero ? (
          <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
            LIBERO
          </span>
        ) : (
          <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
            {canone.codice_listino}
          </span>
        )}
      </td>
      <td className="py-3 text-gray-700 text-sm">
        {isLibero && !disabled ? (
          <input
            type="text"
            value={localDescrizione}
            onChange={(e) => setLocalDescrizione(e.target.value)}
            onBlur={() => {
              if (localDescrizione !== canone.descrizione) {
                onUpdate(canone.id, { descrizione: localDescrizione })
              }
            }}
            className="w-full px-2 py-1 text-sm text-gray-700 bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        ) : (
          canone.descrizione
        )}
      </td>
      <td className="py-3 text-right">
        {disabled ? (
          <span className="font-medium">{canone.quantita}</span>
        ) : (
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={localQta}
            onChange={(e) => setLocalQta(e.target.value)}
            onBlur={() => {
              const q = parseFloat(localQta)
              if (!isNaN(q) && q > 0) {
                onUpdate(canone.id, { quantita: q })
              } else {
                setLocalQta(String(canone.quantita ?? ''))
              }
            }}
            className="w-16 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        )}
      </td>
      <td className="py-3 text-right text-gray-600 text-sm">
        {isLibero && !disabled ? (
          <input
            type="number"
            min="0"
            step="0.01"
            value={localPrezzo}
            onChange={(e) => setLocalPrezzo(e.target.value)}
            onBlur={() => {
              const p = parseFloat(localPrezzo)
              if (!isNaN(p) && p >= 0) {
                onUpdate(canone.id, { prezzo_unitario: p })
              } else {
                setLocalPrezzo(String(canone.prezzo_unitario ?? ''))
              }
            }}
            className="w-20 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        ) : (
          formatCurrency(canone.prezzo_unitario ?? 0)
        )}
      </td>
      <td className="py-3 text-right">
        {disabled ? (
          canone.sconto_percentuale !== 0 && (
            <span className={`text-sm font-medium ${canone.sconto_percentuale > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {canone.sconto_percentuale > 0 ? `-${canone.sconto_percentuale}%` : `+${Math.abs(canone.sconto_percentuale)}%`}
            </span>
          )
        ) : (
          <input
            type="number"
            step="0.5"
            value={localSconto}
            onChange={(e) => setLocalSconto(e.target.value)}
            onBlur={() => {
              const s = parseFloat(localSconto)
              if (!isNaN(s)) {
                onUpdate(canone.id, { sconto_percentuale: s })
              } else {
                setLocalSconto(String(canone.sconto_percentuale || '0'))
              }
            }}
            className="w-16 px-2 py-1 text-right border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        )}
      </td>
      <td className="py-3 text-right font-medium text-gray-900 text-sm">
        {formatCurrency(canone.totale_riga ?? 0)}
      </td>
      <td className="py-3 text-center text-xs text-indigo-600 font-medium whitespace-nowrap">
        ogni {canone.frequenza_valore} {FREQ_LABELS[canone.frequenza_unita] || canone.frequenza_unita}
      </td>
      {!disabled && (
        <td className="py-3 text-right px-2">
          <button
            onClick={() => onDelete(canone.id)}
            className="text-gray-400 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  )
}
